// The "F2 → F3 · tap to switch floors" rectangle anchored at a shaft node.
// Implemented as a raw google.maps.OverlayView (created imperatively by
// useRouteLayer) rather than a React component, so it lives in the map's own
// overlay panes and pans/zooms with the map for free.

import { DETAIL_ZOOM } from '../lib/zoom.js';

/**
 * @param map        google.maps.Map
 * @param position   {lat, lng} — the shaft node the user taps at
 * @param label      e.g. "F2 → F3"
 * @param sub        e.g. "tap to switch floors"
 * @param icon       emoji shown before the label (🪜 / 🛗)
 * @param back       true renders the muted "go back" style
 * @param onTap      click handler
 * @returns {{ remove: () => void }}
 */
/**
 * Non-interactive label shown where the route crosses a building entrance/exit.
 * @param kind  'enter' | 'exit'
 * @param label e.g. "Machray Hall"
 */
export function createBuildingTransitionOverlay(map, position, { kind, label }) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);
  const verb = kind === 'enter' ? 'Enter' : 'Exit';

  class BldgTransitionOverlay extends gmaps.OverlayView {
    onAdd() {
      const div = document.createElement('div');
      div.className = `pfa-floor-switch pfa-floor-switch-${kind}`;
      div.innerHTML =
        `<div class="pfa-bldg-verb">${verb}</div>` +
        `<div class="pfa-bldg-name">${escapeHtml(label)}</div>`;
      ['mousedown', 'touchstart', 'pointerdown', 'click'].forEach(t =>
        div.addEventListener(t, e => e.stopPropagation()));
      this.div = div;
      this.getPanes().overlayMouseTarget.appendChild(div);
    }
    draw() {
      if (!this.div) return;
      const pt = this.getProjection()?.fromLatLngToDivPixel(latLng);
      if (!pt) return;
      this.div.style.left = `${pt.x}px`;
      this.div.style.top = `${pt.y}px`;
    }
    onRemove() { this.div?.remove(); this.div = null; }
  }

  const overlay = new BldgTransitionOverlay();
  overlay.setMap(map);
  return { remove: () => overlay.setMap(null) };
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// A building name is a white box that APPEARS as the map comes in, matching the
// parking map at /parking. It used to do the opposite — solid at campus scale,
// gone by the time you were in close — and the swap is the point: pulled back,
// fifty boxes tile over the campus they are naming, and one box lands across
// four buildings so it names none of them. In close each sits on its own
// footprint. The parking lots carry the pulled-back view instead, with their
// own labels, so there is always something naming what you are looking at.
const BUILDING_IN_ZOOM = 17.5;   // starts fading in
const BUILDING_FULL_ZOOM = 18;   // fully there

/** How solid a building label is at this zoom. */
function labelOpacity(z) {
  if (z == null) return 1;
  if (z <= BUILDING_IN_ZOOM) return 0;
  if (z >= BUILDING_FULL_ZOOM) return 1;
  return (z - BUILDING_IN_ZOOM) / (BUILDING_FULL_ZOOM - BUILDING_IN_ZOOM);
}

/** A building's name, drawn on its footprint as a small white plate. */
export function createBuildingLabelOverlay(map, position, text, { kind = 'building' } = {}) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);
  // Set while something else owns the overview — a route being shown whole.
  // A flag rather than tearing the labels down, because there are ~50 of them
  // and they come straight back.
  let hidden = false;

  class BuildingLabel extends gmaps.OverlayView {
    onAdd() {
      const div = document.createElement('div');
      div.className = `pfa-overview-label pfa-overview-${kind}`;
      div.textContent = text;
      this.div = div;
      // markerLayer, NOT overlayLayer: overlayLayer is where Maps draws the
      // polygons themselves, so a label in it stacks against the building it
      // names by DOM insertion order and loses as soon as that building's
      // interior is added. markerLayer sits above every polygon by definition.
      // Still below overlayMouseTarget and floatPane, so the floor-switch
      // popups, room card and panorama window all stay on top of it — and the
      // label is pointer-events:none, so it never steals a tap from a marker.
      this.getPanes().markerLayer.appendChild(div);
    }
    draw() {
      if (!this.div) return;
      const pt = this.getProjection()?.fromLatLngToDivPixel(latLng);
      if (!pt) return;
      // Ramp rather than a hard cutoff: a label that blinks on or off at a
      // threshold reads as a glitch, one that dissolves reads as the map
      // handing over.
      const op = hidden ? 0 : labelOpacity(map.getZoom());
      // display:none at zero keeps invisible labels out of hit-testing and off
      // the compositor entirely, which matters with ~50 of them on screen.
      this.div.style.display = op <= 0.02 ? 'none' : '';
      this.div.style.opacity = String(op);
      this.div.style.left = `${pt.x}px`;
      this.div.style.top = `${pt.y}px`;
    }
    onRemove() { this.div?.remove(); this.div = null; }
  }

  const overlay = new BuildingLabel();
  overlay.setMap(map);
  return {
    remove: () => overlay.setMap(null),
    setHidden: (v) => { if (hidden === !!v) return; hidden = !!v; overlay.draw(); },
  };
}

/**
 * A parking lot's own label: what the lot is called, what it costs, and what it
 * is doing right now.
 *
 * A plate rather than the haloed text a building name uses, because it carries
 * badges and sits on a coloured polygon — and because the live half is the
 * reason it exists. `setStatus` swaps that half in place as the clock crosses
 * 7:30 or 4:30, so nothing is rebuilt for a word changing.
 *
 * THE PRICE IS DRAWN AT EVERY ZOOM, the way /parking does it. It is the one
 * thing about a lot that does not depend on how close you are or what time it
 * is — someone pulled back over the whole campus choosing where to drive is
 * asking exactly this — so it stays while the full name and the status badge
 * come and go with DETAIL_ZOOM.
 *
 * Pulled back it collapses to ONE LINE — "Q Lot $2.50" — rather than a name
 * with the price stacked under it. Two stacked lines at campus scale is a plate
 * tall enough to cover the lot next door, and the two halves are one thought
 * anyway: which lot, and what it costs. The money alone at that size
 * (`rateShort`), because "/ ½hr (1st hr)" is unreadable there and doubles the
 * width of the box.
 */
export function createParkingLotOverlay(map, position, { name, short, rate, rateShort, colors, status }) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);
  let hidden = false;
  let current = status || null;
  // Which form to draw, pushed in by `setDetail` rather than read off the map
  // here. Both this and the bus stops' departure labels turn over at
  // DETAIL_ZOOM, but reading the zoom inside draw() meant this one flipped
  // *during* a pinch while the bus labels waited for the map to settle — same
  // threshold, visibly different moments, which reads as two different
  // thresholds. Now one listener decides for both. Null until it does, which is
  // the map's own zoom: the label has to be right on the frame it is added.
  let detail = null;

  class LotLabel extends gmaps.OverlayView {
    onAdd() {
      const div = document.createElement('div');
      div.className = 'pfa-lot-label';
      div.style.background = colors?.labelBg || 'rgba(255,255,255,0.94)';
      div.innerHTML =
        `<span class="pfa-lot-name"></span>` +
        `<span class="pfa-lot-badges">` +
        `<span class="pfa-lot-badge"></span>` +
        `<span class="pfa-lot-rate"></span>` +
        `</span>`;
      this.div = div;
      this.nameEl = div.querySelector('.pfa-lot-name');
      this.badgesEl = div.querySelector('.pfa-lot-badges');
      this.badgeEl = div.querySelector('.pfa-lot-badge');
      this.rateEl = div.querySelector('.pfa-lot-rate');
      this.paint();
      // markerLayer for the same reason the building names use it: above every
      // polygon, below the taps.
      this.getPanes().markerLayer.appendChild(div);
    }
    // Guarded, because draw() below runs on every frame of a pan and there are
    // fifteen of these: rewriting identical text into a node each frame is
    // fifteen layout invalidations for a label that has not changed.
    paint() {
      if (!this.div) return;
      const z = map.getZoom();
      const full = detail == null ? (z == null || z >= DETAIL_ZOOM) : detail;
      const sig = `${full ? 1 : 0}|${current?.tone || ''}|${current?.text || ''}`;
      if (sig === this.sig) return;
      this.sig = sig;
      this.div.classList.toggle('collapsed', !full);
      this.nameEl.textContent = full ? name : short;
      // The whole tariff in close, the money alone pulled back.
      const price = full ? rate : (rateShort || rate);
      this.rateEl.textContent = price || '';
      this.rateEl.style.display = price ? '' : 'none';
      this.badgeEl.textContent = current?.text || '';
      this.badgeEl.className = `pfa-lot-badge ${current?.tone || ''}`;
      this.badgeEl.style.display = full && current?.text ? '' : 'none';
      // The row itself disappears when nothing is in it, so a rate-less lot
      // pulled back is a plain name rather than a name with a gap under it.
      this.badgesEl.style.display = (price || (full && current?.text)) ? '' : 'none';
    }
    draw() {
      if (!this.div) return;
      const pt = this.getProjection()?.fromLatLngToDivPixel(latLng);
      if (!pt) return;
      this.paint();
      this.div.style.display = hidden ? 'none' : '';
      this.div.style.left = `${pt.x}px`;
      this.div.style.top = `${pt.y}px`;
    }
    onRemove() { this.div?.remove(); this.div = null; }
  }

  const overlay = new LotLabel();
  overlay.setMap(map);
  return {
    remove: () => overlay.setMap(null),
    setHidden: (v) => { if (hidden === !!v) return; hidden = !!v; overlay.draw(); },
    setStatus: (next) => { current = next; overlay.paint?.(); },
    /** Full name + status badge, or the collapsed one-liner. See `detail`. */
    setDetail: (v) => { if (detail === !!v) return; detail = !!v; overlay.paint?.(); },
  };
}

export function createFloorSwitchOverlay(map, position, { label, sub, icon = '', back = false, onTap }) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);

  class FloorSwitchOverlay extends gmaps.OverlayView {
    onAdd() {
      const div = document.createElement('div');
      div.className = `pfa-floor-switch${back ? ' pfa-floor-switch-back' : ''}`;
      div.innerHTML =
        `<div class="pfa-fs-label">${icon ? `<span class="pfa-fs-icon">${icon}</span>` : ''}${label}</div>` +
        `<div class="pfa-fs-sub">${sub}</div>`;
      div.addEventListener('click', (e) => { e.stopPropagation(); onTap?.(); });
      // Block map gestures from swallowing the tap.
      ['mousedown', 'touchstart', 'pointerdown'].forEach(t =>
        div.addEventListener(t, e => e.stopPropagation()));
      this.div = div;
      this.getPanes().overlayMouseTarget.appendChild(div);
    }
    draw() {
      if (!this.div) return;
      const pt = this.getProjection()?.fromLatLngToDivPixel(latLng);
      if (!pt) return;
      this.div.style.left = `${pt.x}px`;
      this.div.style.top = `${pt.y}px`;
    }
    onRemove() {
      this.div?.remove();
      this.div = null;
    }
  }

  const overlay = new FloorSwitchOverlay();
  overlay.setMap(map);
  return { remove: () => overlay.setMap(null) };
}
